import { z } from "zod";

/**
 * Cohort / area lab insights — DETERMINISTIC population analytics. For patients
 * in a chosen governorate/area, aggregate their lab values to surface the most
 * prevalent abnormal findings. Real SQL aggregates only (no AI guessing);
 * Phase C may layer a natural-language front-end on top of these numbers.
 */

export const cohortInsightQuerySchema = z.object({
	governorate: z.string().max(80).optional(),
	area: z.string().max(120).optional(),
	from: z.string().datetime().optional(),
	to: z.string().datetime().optional(),
	/** Minimum readings for a test to appear (filters noise). */
	min_sample: z.coerce.number().int().min(1).max(1000).default(3),
});
export type CohortInsightQuery = z.infer<typeof cohortInsightQuerySchema>;

export const cohortInsightRowSchema = z.object({
	test_name: z.string(),
	total: z.number().int(), // total readings of this test in the cohort
	abnormal: z.number().int(), // readings flagged != normal
	patients: z.number().int(), // distinct patients tested
	prevalence_pct: z.number(), // abnormal / total × 100
});
export type CohortInsightRow = z.infer<typeof cohortInsightRowSchema>;

export const cohortInsightResponseSchema = z.object({
	governorate: z.string().nullable(),
	area: z.string().nullable(),
	cohort_size: z.number().int(), // distinct patients with any lab value in scope
	total_readings: z.number().int(),
	rows: z.array(cohortInsightRowSchema), // sorted by prevalence desc
});
export type CohortInsightResponse = z.infer<typeof cohortInsightResponseSchema>;

/** Distinct location values for the cohort picker. */
export const cohortLocationsSchema = z.object({
	governorates: z.array(z.string()),
	areas: z.array(z.string()),
});
export type CohortLocationsResponse = z.infer<typeof cohortLocationsSchema>;

// ===== Diagnosis epidemiology (deterministic ICD-based clusters + spikes) =====
//
// For a doctor: aggregate coded diagnoses (ICD-10) by region + time and flag
// SPIKES vs a trailing baseline — catches outbreaks even when no lab test was
// ordered. Pure SQL + arithmetic; transparent (every number is shown), no LLM.

export const diagnosisEpiQuerySchema = z.object({
	governorate: z.string().max(80).optional(),
	area: z.string().max(120).optional(),
	/** "Now" window length in days (the current period). */
	recent_days: z.coerce.number().int().min(1).max(90).default(7),
	/** How many trailing recent-windows form the comparison baseline. */
	baseline_weeks: z.coerce.number().int().min(1).max(26).default(4),
	/** Minimum recent cases for a code to be flagged (filters noise). */
	min_cases: z.coerce.number().int().min(1).max(100).default(3),
});
export type DiagnosisEpiQuery = z.infer<typeof diagnosisEpiQuerySchema>;

export const diagnosisEpiRowSchema = z.object({
	code: z.string(),
	ar: z.string(),
	en: z.string(),
	recent_count: z.number().int(), // coded diagnoses in the recent window
	recent_patients: z.number().int(), // distinct patients
	baseline_avg: z.number(), // avg occurrences per recent-window over the baseline
	ratio: z.number().nullable(), // recent_count / baseline_avg (null when baseline 0)
	is_spike: z.boolean(),
});
export type DiagnosisEpiRow = z.infer<typeof diagnosisEpiRowSchema>;

export const diagnosisEpiResponseSchema = z.object({
	governorate: z.string().nullable(),
	area: z.string().nullable(),
	recent_days: z.number().int(),
	baseline_weeks: z.number().int(),
	total_recent: z.number().int(), // total coded diagnoses in the recent window
	spike_count: z.number().int(),
	rows: z.array(diagnosisEpiRowSchema), // spikes first, then recent_count desc
});
export type DiagnosisEpiResponse = z.infer<typeof diagnosisEpiResponseSchema>;

// ===== Phase C — natural-language "ask your data" =====

export const cohortAskInputSchema = z.object({
	question: z.string().min(2).max(500),
});
export type CohortAskInput = z.infer<typeof cohortAskInputSchema>;

/**
 * The AI parses the question into a location filter, runs the DETERMINISTIC
 * cohort query, then phrases the REAL numbers — `answer` only ever describes
 * figures in `insight` (no invented data). `insight` is the verifiable source.
 */
export const cohortAskResultSchema = z.object({
	answer: z.string(),
	governorate: z.string().nullable(),
	area: z.string().nullable(),
	insight: cohortInsightResponseSchema,
});
export type CohortAskResult = z.infer<typeof cohortAskResultSchema>;
